// Which page exists in which language.
//
// Only a chosen set of pages is translated, not the whole site: the pages a
// reader in another language arrives at and acts on (the home page, the price,
// the free tools, the three pages written for the searches people make before
// they pick an API, the blog) plus a handful of country pages for the markets
// each language serves. The two hundred country pages as a whole, the
// documentation, the cabinet and the legal texts stay English — the legal
// texts because English is the only version that binds (Terms, section 13).
//
// This table is the one answer to "does /es/compare exist", and everything
// that needs it reads it from here: the route guards that refuse an address
// outside it, the alternate links in each page's head, the sitemap and the
// language switcher. A page added to a language is added here and nowhere
// else. Whether a language's pages ask to be indexed is a separate answer
// ($lib/i18n/locales, INDEXED_LOCALES): a page exists for visitors either way.

import {
  DEFAULT_LOCALE,
  isIndexedLocale,
  LOCALE_INFO,
  LOCALES,
  type Locale,
  localePath,
  TRANSLATED_LOCALES,
  type TranslatedLocale,
} from "./locales";

/**
 * The slugs of the blog's articles, read off the file names alone. The glob is
 * not eager, so no article's text is bundled by importing this module; the
 * importers it returns are never called.
 */
const articleFiles = import.meta.glob("../blog/articles/*.md", { query: "?raw" });

export const BLOG_SLUGS: readonly string[] = Object.keys(articleFiles)
  .map((path) => path.replace(/^.*\/(.+)\.md$/, "$1"))
  .sort();

/** The English paths every language carries. */
export const SHARED_PATHS: readonly string[] = [
  "/",
  "/pricing",
  "/mrz-checker",
  "/mrz-generator",
  "/mrz-formats",
  "/free-passport-ocr-api",
  "/compare",
  "/mcp",
  "/blog",
  ...BLOG_SLUGS.map((slug) => `/blog/${slug}`),
];

/**
 * The country pages each language carries: the two English-speaking markets a
 * buyer anywhere compares against, and the markets where that language is the
 * one people search in.
 */
const EVERY_LANGUAGE_COUNTRIES = ["united-kingdom", "united-states"] as const;

export const MARKET_COUNTRIES: Readonly<Record<TranslatedLocale, readonly string[]>> = {
  es: ["spain", "mexico"],
  "pt-br": ["brazil"],
  id: ["indonesia"],
  de: ["germany"],
  fr: ["france"],
  vi: ["vietnam"],
  ja: ["japan"],
  ko: ["south-korea"],
  zh: ["china"],
  tr: ["turkey"],
  ar: ["saudi-arabia", "united-arab-emirates"],
  it: ["italy"],
  hi: ["india"],
};

/** The country slugs a language carries. */
export function countriesFor(locale: TranslatedLocale): readonly string[] {
  return [...EVERY_LANGUAGE_COUNTRIES, ...MARKET_COUNTRIES[locale]];
}

export function countryPagePath(slug: string): string {
  return `/${slug}-passport-ocr`;
}

/**
 * Pages written for one market in that market's language, with no English
 * original and no other language: a page about a Spanish legal duty is read by
 * the people it binds. They carry no alternate links, because they have no
 * alternates.
 */
export const LOCAL_ONLY_PAGES: Readonly<Record<string, TranslatedLocale>> = {
  "/registro-de-viajeros-hoteles": "es",
};

function buildTable(): ReadonlyMap<string, readonly Locale[]> {
  const table = new Map<string, Locale[]>();
  const add = (path: string, locale: Locale): void => {
    const locales = table.get(path) ?? [];
    locales.push(locale);
    table.set(path, locales);
  };
  for (const path of SHARED_PATHS) for (const locale of LOCALES) add(path, locale);
  // Every slug here is a row of the country table; the unit suite checks it
  // rather than this module importing the table, which would put all two
  // hundred rows into the script of every page the switcher is on.
  for (const locale of TRANSLATED_LOCALES) {
    for (const slug of countriesFor(locale)) {
      const path = countryPagePath(slug);
      if (!table.has(path)) add(path, DEFAULT_LOCALE);
      add(path, locale);
    }
  }
  for (const [path, locale] of Object.entries(LOCAL_ONLY_PAGES)) add(path, locale);
  // Kept in the order of LOCALES, so every page lists its alternates the same
  // way round.
  return new Map(
    [...table].map(([path, locales]) => [
      path,
      LOCALES.filter((locale) => locales.includes(locale)),
    ]),
  );
}

const TABLE = buildTable();

/** The languages a page exists in, from its English path; `[]` when it is not in the table. */
export function localesFor(path: string): readonly Locale[] {
  return TABLE.get(path) ?? [];
}

/** Whether a page exists in a language. English exists for every page outside the table. */
export function hasLocale(path: string, locale: Locale): boolean {
  const locales = TABLE.get(path);
  if (locales === undefined) return locale === DEFAULT_LOCALE;
  return locales.includes(locale);
}

/** Every translated address, as `{ locale, path }` pairs, for the prerender entries and the sitemap. */
export function translatedPages(): readonly { locale: TranslatedLocale; path: string }[] {
  const pages: { locale: TranslatedLocale; path: string }[] = [];
  for (const [path, locales] of TABLE) {
    for (const locale of locales) {
      if (locale !== DEFAULT_LOCALE) pages.push({ locale, path });
    }
  }
  return pages;
}

/** One alternate link of a page's head. */
export interface Alternate {
  /** The `hreflang` value: a BCP 47 tag, or `x-default`. */
  readonly hreflang: string;
  /** The site-relative address of that version. */
  readonly href: string;
}

/**
 * The alternate links a page carries: every INDEXED language it exists in,
 * itself included, and `x-default` pointing at the English version for a reader
 * whose language is none of them. Empty for a page that exists in one indexed
 * language only, which has nothing to declare.
 *
 * A language kept out of the index ($lib/i18n/locales, INDEXED_LOCALES) is
 * never named here: an alternate is a page offered to searchers in that
 * language, and a page that says `noindex` is not one. Its own pages carry no
 * alternates at all (SeoHead), so no link in either direction is left
 * one-sided — the pages that do list each other still list each other in full.
 */
export function alternatesFor(path: string): readonly Alternate[] {
  const locales = localesFor(path).filter(isIndexedLocale);
  if (locales.length < 2) return [];
  const links: Alternate[] = locales.map((locale) => ({
    hreflang: LOCALE_INFO[locale].lang,
    href: localePath(locale, path),
  }));
  if (locales.includes(DEFAULT_LOCALE)) links.push({ hreflang: "x-default", href: path });
  return links;
}

/**
 * An internal link as it should read from a page in a language: to that
 * language's version where one exists, to the English page otherwise. A
 * fragment and a query survive; an external or protocol-relative address is
 * returned as it was.
 */
export function localizeHref(href: string, locale: Locale): string {
  if (locale === DEFAULT_LOCALE || !href.startsWith("/") || href.startsWith("//")) return href;
  const match = /^([^?#]*)(.*)$/.exec(href);
  const path = match?.[1] ?? href;
  const rest = match?.[2] ?? "";
  if (path === "" || !hasLocale(path, locale)) return href;
  return `${localePath(locale, path)}${rest}`;
}

/**
 * Every `href="/…"` of a fragment of HTML, localized the same way. Used for
 * translated prose that carries its own links, such as a blog article or a
 * paragraph with a link inside it.
 */
export function localizeLinks(html: string, locale: Locale): string {
  if (locale === DEFAULT_LOCALE) return html;
  return html.replace(/href="(\/[^"]*)"/g, (_whole, href: string) => {
    return `href="${localizeHref(href, locale)}"`;
  });
}

/**
 * Where the switcher sends a reader who picks a language while on `path`: the
 * same page in that language if it exists, that language's home otherwise.
 */
export function switchTarget(path: string, locale: Locale): string {
  return hasLocale(path, locale) ? localePath(locale, path) : localePath(locale, "/");
}
